/**
 * LLMController - WebLLM Integration Service
 *
 * Manages local Large Language Model (WebLLM) functionality:
 * - Model download and caching
 * - Initialization and engine management
 * - Streaming chat completions
 * - Progress tracking and error handling
 *
 * Singleton pattern - use LLMController.method() to access
 *
 * PubSub Integration:
 * - Publishes to: LLM_STATUS, LLM_COMPLETE, LLM_ERROR, LLM_RESPONSE
 * - Subscribes to: LLM_DO_DOWNLOAD, LLM_PROMPT, LLM_CANCEL
 */

import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { SystemPromptProvider, newId } from '../../../../shared/conversation.js';
import {
  toRoutingGrammar,
  routingPrompt,
  decideFromMarks,
  toActionSchemaJSON,
  paramsFor,
  NO_TEMPLATE
} from '../../../../shared/meta/meta_templates.js';

class LLMController {
  // Singleton state
  static engine = null;
  static webllm = null;
  static isInitialized = false;
  static currentModel = null;
  static isGenerating = false;
  static isModelCached = false; // Simple boolean - we only use one model

  // Single model for this application
  // Hermes-2-Pro-Llama-3-8B supports function calling
  static DEFAULT_MODEL = 'Hermes-2-Pro-Llama-3-8B-q4f32_1-MLC';

  // Transcript: user/assistant turns ONLY.
  //
  // The system message is deliberately not stored here. It is composed fresh on
  // every request by SystemPromptProvider and prepended at send time, so it can
  // vary per turn (route, entity role, feature flags) without rewriting history
  // the model has already seen - which would both contradict earlier turns and
  // invalidate the engine's prefix cache from position 0.
  static history = [];

  // Sliding window over the transcript. Without it `history` grows for the life
  // of the page and eventually exceeds the model's context window - check
  // `webllm.prebuiltAppConfig` for this build's `context_window_size` before
  // raising it, since MLC often trims it below the base model's limit for VRAM.
  //
  // Counted in messages, not exchanges, and trimmed in pairs by trimHistory().
  static MAX_HISTORY_MESSAGES = 24;

  /** Last routing decision, marks included. Debug surface for `::template route`. */
  static lastRouting = null;

  /**
   * Append an assistant turn the model did not itself produce.
   *
   * A template rendered into the canvas is invisible to the model - it never saw
   * the markup - so the workflow sink calls this with a short factual summary.
   * Skipping it breaks the very next turn: "sort that by spend" has no referent.
   */
  static noteAssistant(text) {
    if (!text) return;
    this.history.push({ role: 'assistant', content: String(text) });
    this.trimHistory();
  }

  /**
   * Append a user turn that bypassed chat().
   *
   * A prompt answered by a rendered template never reaches chat(), so nothing
   * else records the question. Without this the transcript accumulates lone
   * assistant notes describing views that answer nothing, and trimHistory()
   * eventually discards them for having no question in front of them.
   */
  static noteUser(text) {
    if (!text) return;
    this.history.push({ role: 'user', content: String(text) });
    this.trimHistory();
  }

  /**
   * Drop the oldest messages past the window.
   *
   * Trims to a user turn, never leaving the transcript starting on an assistant
   * message - a dangling answer with no question reads as the model having
   * asserted something unprompted, and some chat templates reject the ordering
   * outright.
   */
  static trimHistory() {
    if (this.history.length <= this.MAX_HISTORY_MESSAGES) return;

    const dropped = this.history.length - this.MAX_HISTORY_MESSAGES;
    this.history = this.history.slice(dropped);

    while (this.history.length && this.history[0].role !== 'user') {
      this.history.shift();
    }

    console.log(`[LLMController] Transcript trimmed to ${this.history.length} messages`);
  }

  /**
   * Context handed to the system prompt contributors. Extend freely - anything
   * a contributor might condition on.
   */
  static systemContext() {
    return {
      entity: globalThis.window?.AuthController?.getEntity?.() ?? null,
      route: globalThis.location?.hash ?? '',
      modelId: this.DEFAULT_MODEL
    };
  }

  /**
   * Initialize the WebLLM module and engine
   * Must be called before any other operations
   */
  static async initialize() {
    if (this.isInitialized) {
      console.log('[LLMController] Already initialized');
      return true;
    }

    console.log('[LLMController] Initializing WebLLM module...');

    try {
      // Check WebGPU support
      if (!navigator.gpu) {
        throw new Error('WebGPU is not available. Please use a supported browser (Chrome 113+, Edge 113+).');
      }
      console.log('[LLMController] WebGPU detected');

      // Load WebLLM module from CDN
      this.webllm = await import('https://esm.run/@mlc-ai/web-llm');
      console.log('[LLMController] WebLLM module loaded');

      // Verify MLCEngine exists
      if (!this.webllm || !this.webllm.MLCEngine) {
        throw new Error('MLCEngine not found in WebLLM module');
      }

      // Create engine instance
      this.engine = new this.webllm.MLCEngine();
      console.log('[LLMController] MLCEngine created');

      // Set progress callback to publish status updates
      this.engine.setInitProgressCallback((report) => {
        this.handleInitProgress(report);
      });

      // Check if our model is cached
      this.isModelCached = await this.checkModelCache();

      // Mark as initialized BEFORE auto-load (so downloadModel works)
      this.isInitialized = true;
      console.log('[LLMController] Engine initialized, attempting auto-load...');

      // Auto-load cached model if found
      await this.autoLoadCachedModel();

      console.log('[LLMController] Initialization complete');
      return true;

    } catch (error) {
      console.error('[LLMController] Initialization failed:', error);
      PubSub.publish(AppBroadcast.LLM_ERROR, {
        error: error.message,
        phase: 'initialization'
      });
      return false;
    }
  }

  /**
   * Check if our single model is cached
   * @returns {boolean} True if model files found in cache
   */
  static async checkModelCache() {
    console.log(`[LLMController] Checking cache for model: ${this.DEFAULT_MODEL}`);

    try {
      // Check Cache API directly - look for webllm/model cache
      const cacheNames = await caches.keys();
      const webllmModelCache = cacheNames.find(name => name.includes('webllm/model'));

      if (!webllmModelCache) {
        console.log('[LLMController] No WebLLM model cache found');
        return false;
      }

      const cache = await caches.open(webllmModelCache);
      const cachedRequests = await cache.keys();
      const modelFiles = cachedRequests.filter(req =>
        req.url.includes(this.DEFAULT_MODEL) || req.url.includes('Hermes-2-Pro-Llama-3-8B')
      );

      const isCached = modelFiles.length > 0;
      console.log(`[LLMController] Model cached: ${isCached} (${modelFiles.length} files)`);
      return isCached;

    } catch (err) {
      console.warn(`[LLMController] Cache check failed:`, err.message);
      return false;
    }
  }

  /**
   * Auto-load cached model on initialization
   * If model is not cached, automatically download it
   */
  static async autoLoadCachedModel() {
    try {
      if (this.isModelCached) {
        console.log(`[LLMController] Loading cached model: ${this.DEFAULT_MODEL}`);
      } else {
        console.log(`[LLMController] No cached model found - starting download: ${this.DEFAULT_MODEL}`);
      }

      await this.loadModel();
      console.log('[LLMController] Model ready');
    } catch (error) {
      console.error('[LLMController] Auto-load failed:', error);
      // Don't throw - non-critical error
    }
  }

  /**
   * Load the model (download if not cached, or load from cache)
   * This is the only model-loading method - simplified for single-model use
   */
  static async loadModel() {
    if (!this.isInitialized) {
      console.error('[LLMController] Cannot load model: not initialized');
      PubSub.publish(AppBroadcast.LLM_ERROR, {
        error: 'LLMController not initialized',
        modelId: this.DEFAULT_MODEL
      });
      return false;
    }

    const wasCached = this.isModelCached;
    console.log(`[LLMController] Loading model: ${this.DEFAULT_MODEL} (cached: ${wasCached})`);

    try {
      // Reload engine with our model (downloads if needed, or loads from cache)
      const chatConfig = {
        temperature: 0.7,
        top_p: 0.95
      };

      await this.engine.reload(this.DEFAULT_MODEL, chatConfig);

      this.currentModel = this.DEFAULT_MODEL;
      this.isModelCached = true;

      console.log(`[LLMController] Model loaded successfully: ${this.DEFAULT_MODEL}`);

      // Publish completion event
      PubSub.publish(AppBroadcast.LLM_COMPLETE, {
        modelId: this.DEFAULT_MODEL,
        cached: wasCached
      });

      return true;

    } catch (error) {
      console.error(`[LLMController] Failed to load model:`, error);
      PubSub.publish(AppBroadcast.LLM_ERROR, {
        error: error.message,
        modelId: this.DEFAULT_MODEL,
        phase: 'download'
      });
      return false;
    }
  }

  /**
   * Handle initialization progress callback
   * Publishes progress updates via PubSub
   */
  static handleInitProgress(report) {
    const progressPercent = (report.progress * 100).toFixed(1);
    let progressText = report.text;

    // Format progress text
    if (progressPercent > 0 || !report.text.includes('Loading model from cache')) {
      progressText = `${progressPercent}% - ${report.text}`;
    }

    console.log(`[LLMController] Progress: ${progressText}`);

    // Publish status update
    PubSub.publish(AppBroadcast.LLM_STATUS, {
      progress: report.progress,
      progressPercent: parseFloat(progressPercent),
      text: progressText,
      modelId: this.currentModel || this.DEFAULT_MODEL
    });
  }

  /**
   * Send a chat message and get streaming response
   * @param {string} userMessage - User's message
   */
  static async chat(userMessage, options = {}) {
    const { turnId } = options;

    // One id for this generation - every frame carries it so the canvas
    // addresses exactly one bubble, however many streams are in flight.
    const responseId = newId('res');

    if (!this.currentModel) {
      console.error('[LLMController] No model loaded - still downloading or initialization failed');
      PubSub.publish(AppBroadcast.LLM_ERROR, {
        error: 'AI model is still loading. Please wait a moment and try again.',
        phase: 'chat',
        turnId,
        responseId
      });
      return;
    }

    if (this.isGenerating) {
      console.warn('[LLMController] Already generating response');
      return;
    }

    console.log(`[LLMController] Processing chat message: "${userMessage}"`);

    // System message resolved now, for this request only.
    const systemContent = SystemPromptProvider.compose(this.systemContext());
    const systemHash = SystemPromptProvider.hash(systemContent);

    this.history.push({ role: 'user', content: userMessage });
    this.trimHistory();

    const messages = [{ role: 'system', content: systemContent }, ...this.history];

    this.isGenerating = true;
    let currentResponse = '';
    let usage = null;

    try {
      // Create streaming completion
      const completion = await this.engine.chat.completions.create({
        stream: true,
        messages,
        stream_options: { include_usage: true }
      });

      // Process stream
      for await (const chunk of completion) {
        const deltaContent = chunk.choices[0]?.delta?.content;

        if (deltaContent) {
          currentResponse += deltaContent;

          // Publish response chunk
          PubSub.publish(AppBroadcast.LLM_RESPONSE, {
            content: deltaContent,
            fullContent: currentResponse,
            done: false,
            turnId,
            responseId,
            meta: { model: this.DEFAULT_MODEL, systemHash }
          });
        }

        // Capture usage stats
        if (chunk.usage) {
          usage = chunk.usage;
          console.log('[LLMController] Usage stats:', chunk.usage);
        }
      }

      // Add assistant response to history
      this.history.push({ role: 'assistant', content: currentResponse });
      this.trimHistory();

      // Publish completion
      PubSub.publish(AppBroadcast.LLM_RESPONSE, {
        content: '',
        fullContent: currentResponse,
        done: true,
        turnId,
        responseId,
        meta: { model: this.DEFAULT_MODEL, systemHash, usage }
      });

      console.log(`[LLMController] Response complete (${currentResponse.length} chars)`);

    } catch (error) {
      console.error('[LLMController] Chat error:', error);
      PubSub.publish(AppBroadcast.LLM_ERROR, {
        error: error.message,
        phase: 'chat',
        turnId,
        responseId
      });
    } finally {
      this.isGenerating = false;
    }
  }

  /**
   * Routing pass - ask which registered view (if any) this request wants.
   *
   * Called by routeThenPrompt() in app_events.js ahead of every agent-bound
   * prompt, behind FF_CLIENT_TEMPLATE_ROUTING. A hit renders a template instead
   * of generating prose; anything else falls through to chat().
   *
   * The answer is grammar-constrained to the schema the registry generates, so
   * the model *cannot* name a view that is not registered - this is enforced at
   * sampling, not by asking nicely in the prompt. Small models are unreliable at
   * "reply in JSON"; they are exactly reliable under a grammar.
   *
   * Costs a second prefill. WebLLM's KV cache does not carry across a differing
   * message set, so this is not free - keep the tail short and the output tiny.
   * Returns null on any failure, which the caller should read as "answer in
   * prose": a routing miss must never cost the user their answer.
   *
   * @param {string} userMessage
   * @returns {Promise<{componentId: string, params: object} | null>}
   */
  static async route(userMessage) {
    // These two are "could not run", NOT "decided not to route" - but both used to
    // return null, which routeThenPrompt cannot tell apart from an honest NONE. The
    // turn then fell through to prose and looked like a routing miss, sending us
    // tuning `when` lines that were never consulted. Say which happened.
    if (!this.currentModel) {
      console.warn('[LLMController] routing SKIPPED - model not loaded yet (answering in prose)');
      return null;
    }

    if (this.isGenerating) {
      // Something else is mid-generation. An SSE message can publish LLM.PROMPT
      // (MachVive forwards any topic the server sends), so this can happen with
      // no user action at all.
      console.warn('[LLMController] routing SKIPPED - a generation is already in flight (answering in prose)');
      return null;
    }

    // No transcript, no history tail, nothing from the conversation - see
    // routingPrompt(). Routing is a pure function of this one message, so the
    // same sentence always routes the same way and a `when` edit is measurable.
    //
    // NOT SystemPromptProvider.compose() either: reusing the chat prompt put
    // "be helpful" in front of a decision whose right answer is usually to do
    // nothing.
    const system = routingPrompt();
    const grammar = toRoutingGrammar();
    if (!system || !grammar) return null; // nothing registered - no decision to make

    const messages = [
      { role: 'system', content: system },
      { role: 'user', content: userMessage }
    ];

    try {
      const completion = await this.engine.chat.completions.create({
        stream: false,
        messages,
        // The grammar terminates on its own; this only has to exceed the longest
        // valid output, which grows with the number of registered views. Hitting
        // it mid-JSON fails the parse and falls through to prose.
        max_tokens: 256,
        // Greedy. WebLLM takes argmax at exactly 0.0; omit this and it inherits
        // the MODEL's chat-config temperature, which is non-zero - so routing was
        // sampling a distribution rather than picking a winner, and the same
        // prompt returned different views across page loads. Classification wants
        // the argmax; only the prose path in chat() should stay stochastic.
        temperature: 0,
        // EBNF rather than a JSON schema - see toRoutingGrammar(). The engine
        // masks out-of-grammar tokens on the GPU before sampling, so the model
        // cannot emit an id that is not registered.
        //
        // Do NOT add `tools` alongside this: Hermes-2-Pro function calling
        // overwrites response_format with its own schema and throws
        // CustomResponseFormatError if a custom one is present. They are
        // mutually exclusive.
        response_format: {
          type: 'grammar',
          grammar
        }
      });

      const raw = completion.choices[0]?.message?.content;
      if (!raw) return null;

      console.log('[LLMController] routing raw:', raw);

      const parsed = JSON.parse(raw);

      // The model marks; the decision is arithmetic. It used to also name a
      // winner, and the winner contradicted its own marks - see toRoutingGrammar.
      const decision = decideFromMarks(parsed);
      const id = decision.id;

      // Kept for `::template route`, so the probe can show WHY it decided rather
      // than only what it decided. Reading the marks is the difference between
      // diagnosing this and guessing at the prompt again.
      this.lastRouting = { raw, parsed, committed: id };

      if (decision.ambiguous) {
        console.warn(`[LLMController] marks were ambiguous [${decision.marked.join(', ')}] - declining`);
      }

      // A DECLINE is returned, not nulled. null now means "the pass could not
      // run" - model loading, a generation in flight, a thrown request - and the
      // caller must treat those differently: declining to help someone because
      // the model had not finished loading would be a lie.
      if (!id || id === NO_TEMPLATE) {
        console.log(`[LLMController] Routed to: ${NO_TEMPLATE} - out of scope`);
        return { componentId: NO_TEMPLATE, params: {} };
      }

      console.log(`[LLMController] Routed to: ${id}  marks [${decision.marked.join(', ')}]`);
      return { componentId: id, params: {} };
    } catch (error) {
      console.warn('[LLMController] Routing pass failed - falling through to prose', error);
      return null;
    }
  }

  /**
   * Second pass: pull an action's arguments out of the user's message.
   *
   * Only runs once routing has already chosen an action, so the schema is tiny
   * and specific to that one action rather than a union over every action the
   * app knows. That is the whole reason for splitting the passes - a combined
   * grammar would have to admit every argument shape at once, and the model
   * would be choosing an intent and filling a form in a single commit.
   *
   * JSON Schema here rather than the EBNF used for routing: arguments are free
   * strings with no enum to mistranslate, and a hand-written EBNF would need
   * bounded repetition rules that risk truncating mid-JSON.
   *
   * Returns `{}` on any failure, which the caller should treat as "nothing
   * extracted" - validation downstream decides whether that is fatal.
   *
   * @returns {Promise<object>} raw extracted params, NOT yet validated
   */
  static async extractParams(componentId, actionId, userMessage) {
    // actionId may be null: a VIEW can declare params too (APP_SNIPPET needs a
    // name), and paramsFor reads either.
    const params = paramsFor(componentId, actionId);
    if (!params || !Object.keys(params).length) return {};
    if (!this.currentModel || this.isGenerating) return {};

    const schema = toActionSchemaJSON(componentId, actionId);
    if (!schema) return {};

    const names = Object.keys(params).join(', ');

    const messages = [
      {
        role: 'system',
        content: [
          `Extract these values from the user's message: ${names}.`,
          'Copy them exactly as the user wrote them. Do not invent, complete, or correct a value.',
          'If a value is not present in the message, return an empty string for it.'
        ].join('\n')
      },
      { role: 'user', content: userMessage }
    ];

    try {
      const completion = await this.engine.chat.completions.create({
        stream: false,
        messages,
        max_tokens: 256,
        temperature: 0,
        response_format: { type: 'json_object', schema }
      });

      const raw = completion.choices[0]?.message?.content;
      console.log('[LLMController] extract raw:', raw);

      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.warn('[LLMController] extraction failed', error);
      return {};
    }
  }

  /**
   * Cancel ongoing generation
   */
  static cancelGeneration() {
    if (!this.isGenerating || !this.engine) {
      console.warn('[LLMController] Nothing to cancel');
      return;
    }

    console.log('[LLMController] Cancelling generation...');

    try {
      this.engine.interruptGenerate();
      this.isGenerating = false;
      console.log('[LLMController] Generation cancelled');
    } catch (error) {
      console.error('[LLMController] Cancel failed:', error);
    }
  }

  /**
   * Get current model status
   * @returns {Object} Model status details
   */
  static getStatus() {
    return {
      modelId: this.DEFAULT_MODEL,
      isLoaded: !!this.currentModel,
      isCached: this.isModelCached,
      isGenerating: this.isGenerating,
      messageCount: this.history.length
    };
  }

  /**
   * Reset conversation history. The system prompt is not touched - it lives in
   * SystemPromptProvider and is recomposed on the next request.
   */
  static resetConversation() {
    this.history = [];
    console.log('[LLMController] Conversation reset');
  }
}

export default LLMController;
